import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import AppellationMapViewer from '../../../components/appellations/AppellationMapViewer';
import VarietalAgingOverrides from '../../../components/appellations/VarietalAgingOverrides';
import CopyFields from '../../../components/CopyFields';
import client from '../../../lib/feathers';

const DESIGNATION_COLORS = {
  ava: 'bg-blue-100 text-blue-800',
  aoc: 'bg-purple-100 text-purple-800',
  aop: 'bg-purple-100 text-purple-800',
  doc: 'bg-green-100 text-green-800',
  docg: 'bg-emerald-100 text-emerald-800',
  do: 'bg-yellow-100 text-yellow-800',
  doca: 'bg-yellow-100 text-yellow-800',
  gi: 'bg-orange-100 text-orange-800',
  wo: 'bg-pink-100 text-pink-800',
  vqa: 'bg-red-100 text-red-800',
  ig: 'bg-teal-100 text-teal-800',
};

export default function AppellationDetail() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [appellation, setAppellation] = useState(null);
  const [children, setChildren] = useState([]);
  const [listings, setListings] = useState([]);
  const [parentChain, setParentChain] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  // The public copy, edited separately from everything else on this page. This
  // page's only other write is a boundary patch, and a text save must not drag
  // a MULTIPOLYGON along with it: patching `boundary` re-runs listing
  // assignment, so saving a typo fix would silently reassign wineries.
  const [copy, setCopy] = useState({ summary: '', description: '' });
  const [copySaving, setCopySaving] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const [copySaved, setCopySaved] = useState(false);

  const loadAppellation = async () => {
    try {
      setLoading(true);
      setError(null);
      const service = client.service('admin/appellation');
      const result = await service.get(aid);
      setAppellation(result);
      setCopy({ summary: result.summary || '', description: result.description || '' });
      setCopySaved(false);

      // Load children
      const childResult = await service.find({
        query: { parentId: aid, $limit: 100, $sort: { name: 1 } }
      });
      setChildren(childResult.data);

      // Load assigned listings with names
      const laService = client.service('admin/listing-appellation');
      const laResult = await laService.find({
        query: { appellationId: aid, $limit: 200 }
      });
      // API already filters out Non-Industry listings and includes listing names
      // Backfill listing names for any records missing the include
      const listingIds = laResult.data.filter(la => !la.listing).map(la => la.listingId).filter(Boolean);
      if (listingIds.length > 0) {
        const listingService = client.service('admin/listing');
        const listingResult = await listingService.find({
          query: { id: { $in: listingIds }, $limit: listingIds.length, $select: ['id', 'name'] }
        });
        const nameMap = {};
        listingResult.data.forEach(l => { nameMap[l.id] = l.name; });
        laResult.data.forEach(la => {
          if (!la.listing) la.listing = { id: la.listingId, name: nameMap[la.listingId] || la.listingId };
        });
      }
      setListings(laResult.data);

      // Build parent chain
      const chain = [];
      let current = result;
      while (current.parentId) {
        try {
          const parent = await service.get(current.parentId);
          chain.unshift(parent);
          current = parent;
        } catch {
          break;
        }
      }
      setParentChain(chain);
    } catch (e) {
      console.error('Error loading appellation:', e);
      setError('Failed to load appellation.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopySave = async () => {
    try {
      setCopySaving(true);
      setCopyError(null);
      const service = client.service('admin/appellation');
      // Only the two text fields. See the note on the copy state above.
      const saved = await service.patch(aid, {
        summary: copy.summary.trim() || null,
        description: copy.description.trim() || null,
      });
      setAppellation(saved);
      setCopy({ summary: saved.summary || '', description: saved.description || '' });
      setCopySaved(true);
    } catch (e) {
      console.error('Error saving copy:', e);
      setCopyError(e.message || 'Could not save.');
    } finally {
      setCopySaving(false);
    }
  };

  const handleReassign = async () => {
    if (!window.confirm('Re-run auto-assignment for this appellation? This will update listing associations based on the current boundary.')) return;
    try {
      setReassigning(true);
      // Trigger re-assignment by patching with the existing boundary
      const service = client.service('admin/appellation');
      await service.patch(aid, { boundary: appellation.boundary });
      await loadAppellation();
    } catch (e) {
      console.error('Error reassigning:', e);
      alert('Failed to re-run assignment.');
    } finally {
      setReassigning(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${appellation.name}"? This cannot be undone.`)) return;
    try {
      const service = client.service('admin/appellation');
      await service.remove(aid);
      navigate('/admin/appellations');
    } catch (e) {
      console.error('Error deleting:', e);
      alert(e.message || 'Failed to delete appellation.');
    }
  };

  useEffect(() => {
    loadAppellation();
  }, [aid]);

  if (loading) {
    return (
      <Page title="Loading...">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Page>
    );
  }

  if (error || !appellation) {
    return (
      <Page title="Error">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Appellation not found.'}</p>
        </div>
      </Page>
    );
  }

  const copyDirty = !!appellation && (
    (copy.summary || '') !== (appellation.summary || '')
    || (copy.description || '') !== (appellation.description || '')
  );

  const designationColor = DESIGNATION_COLORS[appellation.designationType] || 'bg-gray-100 text-gray-800';
  const meta = appellation.meta || {};

  return (
    <Page title={appellation.name}>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Appellations', href: '/admin/appellations' },
            ...parentChain.map(p => ({ name: p.name, href: `/admin/appellations/${p.id}` })),
            { name: appellation.name }
          ]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4 flex gap-2">
          {appellation.boundary && (
            <button
              type="button"
              onClick={handleReassign}
              disabled={reassigning}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {reassigning ? 'Assigning...' : 'Re-run Assignment'}
            </button>
          )}
          <Link
            to={`/admin/appellations/${aid}/edit`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            Edit
          </Link>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Header Info */}
      <div className="mt-6 bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-2xl font-bold text-gray-900">{appellation.name}</h2>
          {appellation.shortName && (
            <span className="text-lg text-gray-400">({appellation.shortName})</span>
          )}
          <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full uppercase ${designationColor}`}>
            {appellation.designationType}
          </span>
          {!appellation.active && (
            <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
              Inactive
            </span>
          )}
        </div>
        {parentChain.length > 0 && (
          <div className="mb-4">
            <dt className="text-sm font-medium text-gray-500 mb-1">Parent Appellation</dt>
            <dd className="flex items-center gap-1 text-sm">
              {parentChain.map((p, i) => (
                <span key={p.id} className="flex items-center gap-1">
                  {i > 0 && <span className="text-gray-400">&rsaquo;</span>}
                  <Link to={`/admin/appellations/${p.id}`} className="text-primary hover:text-red-900 font-medium">
                    {p.name}
                  </Link>
                </span>
              ))}
            </dd>
          </div>
        )}
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Country</dt>
            <dd className="mt-1 text-sm text-gray-900">{appellation.country}</dd>
          </div>
          {meta.state && (
            <div>
              <dt className="text-sm font-medium text-gray-500">State</dt>
              <dd className="mt-1 text-sm text-gray-900">{meta.state}</dd>
            </div>
          )}
          {meta.county && (
            <div>
              <dt className="text-sm font-medium text-gray-500">County</dt>
              <dd className="mt-1 text-sm text-gray-900">{meta.county.replace(/\|/g, ', ')}</dd>
            </div>
          )}
          {meta.created && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Established</dt>
              <dd className="mt-1 text-sm text-gray-900">{meta.created}</dd>
            </div>
          )}
          {meta.aka && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Also Known As</dt>
              <dd className="mt-1 text-sm text-gray-900">{meta.aka}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">Boundary</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {appellation.boundary ? 'Yes' : 'Not set'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Hierarchy Depth</dt>
            <dd className="mt-1 text-sm text-gray-900">{appellation.depth}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Listings</dt>
            <dd className="mt-1 text-sm text-gray-900">{listings.length}</dd>
          </div>
        </dl>

        {/* Regulatory Details */}
        {(meta.petitioner || meta.cfr_index || meta.cfr_revision_history) && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Regulatory Details</h4>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {meta.petitioner && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Petitioner</dt>
                  <dd className="mt-1 text-sm text-gray-900">{meta.petitioner}</dd>
                </div>
              )}
              {meta.cfr_author && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">CFR Author</dt>
                  <dd className="mt-1 text-sm text-gray-900">{meta.cfr_author}</dd>
                </div>
              )}
              {meta.cfr_index && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">CFR Index</dt>
                  <dd className="mt-1 text-sm text-gray-900">{meta.cfr_index}</dd>
                </div>
              )}
              {meta.cfr_revision_history && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">CFR Revision History</dt>
                  <dd className="mt-1 text-sm text-gray-900">{meta.cfr_revision_history}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Boundary Description */}
        {meta.boundary_description && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Official Boundary Description</h4>
            <p className="text-sm text-gray-700 whitespace-pre-line max-h-48 overflow-y-auto bg-gray-50 rounded p-3">
              {meta.boundary_description}
            </p>
          </div>
        )}

        {/* Maps */}
        {meta.approved_maps && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Approved Maps</h4>
            <p className="text-sm text-gray-700">{meta.approved_maps}</p>
          </div>
        )}

        {/* Contains */}
        {meta.contains && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Contains</h4>
            <p className="text-sm text-gray-700">{meta.contains.replace(/\|/g, ', ')}</p>
          </div>
        )}
      </div>

      {/* Public copy */}
      <div className="mt-6 bg-white shadow sm:rounded-lg p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Public copy</h3>
          <span className="text-xs text-gray-500">
            Shown on the public appellation page
          </span>
        </div>
        <div className="space-y-4">
          <CopyFields
            idPrefix="appellation"
            summary={copy.summary}
            description={copy.description}
            descriptionRows={7}
            onChange={(field, value) => {
              setCopy((c) => ({ ...c, [field]: value }));
              setCopySaved(false);
            }}
          />
        </div>
        {copyError && (
          <p className="mt-3 text-sm text-red-700">{copyError}</p>
        )}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleCopySave}
            disabled={copySaving || !copyDirty}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-800 hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copySaving ? 'Saving...' : 'Save copy'}
          </button>
          {!copyDirty && copySaved && (
            <span className="text-sm text-green-700">Saved.</span>
          )}
          {copyDirty && (
            <span className="text-sm text-gray-500">Unsaved changes.</span>
          )}
        </div>
      </div>

      {/* Map */}
      {appellation.boundary && (
        <div className="mt-6 bg-white shadow sm:rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Boundary</h3>
          <AppellationMapViewer appellationId={aid} />
        </div>
      )}

      {/* Varietal Aging Overrides */}
      <VarietalAgingOverrides appellationId={aid} appellationName={appellation.name} />

      {/* Children */}
      <div className="mt-6 bg-white shadow sm:rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Sub-Appellations ({children.length})
        </h3>
        {children.length === 0 ? (
          <p className="text-sm text-gray-500">No sub-appellations.</p>
        ) : (
          <div className="space-y-2">
            {children.map(child => (
              <Link
                key={child.id}
                to={`/admin/appellations/${child.id}`}
                className="block p-3 rounded-md hover:bg-gray-50 border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{child.name}</span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${DESIGNATION_COLORS[child.designationType] || 'bg-gray-100 text-gray-800'}`}>
                      {child.designationType}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">{child.country}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Assigned Listings */}
      <div className="mt-6 bg-white shadow sm:rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Assigned Listings ({listings.length})
        </h3>
        {listings.length === 0 ? (
          <p className="text-sm text-gray-500">No listings assigned to this appellation.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Listing</th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {listings.map(la => (
                  <tr key={la.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">
                      {la.listing ? (
                        <Link to={`/admin/listings/${la.listingId}`} className="text-primary hover:text-red-900">
                          {la.listing.name}
                        </Link>
                      ) : (
                        <span className="text-gray-500">{la.listingId}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${la.source === 'auto' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {la.source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Page>
  );
}
